<template>

  <div class="relative flex items-center justify-center w-full min-w-full flex-col" >

  <div v-if="!layoutReady" class="grid grid-cols-1 gap-4 w-full">
    <div v-for="n in props.maxVisible" :key="n" class="rounded-lg bg-gray-600/60 animate-pulse aspect-[3/2] max-h-[80dvh]" ></div>
  </div>
  <div v-else-if="showMedia && preloadedItems?.length === 1">
      <template v-if="preloadedItems[0]?.type === 'image'">
        <img
            :src="preloadedItems[0]?.resolvedUrl"
            v-bind="imgAttrs(preloadedItems[0]?.resolvedUrl, '100vw')"
            :alt="imgAlt(preloadedItems[0]?.resolvedUrl, captionText(preloadedItems[0]), `Media 1`)"
            class=" w-full h-full object-contain max-h-[65dvh]  "
            :class="mediaRadiusClass"
        />
        <p v-if="showCaptions && captionText(preloadedItems[0])" class="gallery-caption-below">{{ captionText(preloadedItems[0]) }}</p>
      </template>
      <template v-else-if="preloadedItems[0]?.type === 'video'">
        <video
            :src="preloadedItems[0]?.resolvedUrl"
            v-bind="imgAttrs(preloadedItems[0]?.resolvedUrl, '100vw')"
            preload="metadata"
            :poster="preloadedItems[0]?.video.poster"
            controls
            class="w-full h-full object-contain max-h-[65dvh] rounded-sm"
        ></video>
      </template>
  </div>
  <MasonryWall
      v-else-if="showMedia"
      ref="masonryRef"
      :items="preloadedItems"
      :column-width="300"
      :gap="16"
      :min-columns="1"
      :max-columns="5"
      :class="wallClass"
      style="width: 100%" :style="cssVars"
  >
    <template #default="{ item, index }">
      <div
        class="rounded-lg overflow-hidden relative max-h-[80dvh]"
        :style="{
          width: '100%',
          // For Masonry's measurement: height is min(calculated, 80dvh)
          height: 'min(calc(' + (300 / (item.aspectRatio || 1)) + 'px), 80dvh)',
          overflow: 'hidden'
        }"
      >
        <!-- Skeleton placeholder as absolute overlay -->
        <div
          v-if="!loadedFlags[index]"
          class="absolute inset-0 rounded-lg animate-pulse w-full h-full max-h-[80dvh]"
          style="max-height: 100dvh; width: 100%;"
        />
        <!-- Real media with fade transition -->
        <transition name="fade">
          <div v-show="loadedFlags[index] && showMedia" @click="openAllLightbox(index)" class="absolute flex items-center justify-center rounded-lg inset-0 w-full h-full max-h-[80dvh] " :style="cssVars">
            <template v-if="item.type === 'image'">
              <img
                  :src="item.resolvedUrl"
                  v-bind="imgAttrs(item.resolvedUrl, '(min-width: 1024px) 33vw, 50vw')"
                  :alt="imgAlt(item.resolvedUrl, captionText(item), `Media ${index + 1}`)"
                  :class="mediaRadiusClass" :style="{height: '100%'}"
              />
            </template>
            <template v-else-if="item.type === 'video'">
              <video
                :src="item.resolvedUrl"
                preload="metadata"
                :poster="item.video.poster"
                controls
                class="absolute inset-0 w-full h-full masonry-media"
              ></video>
            </template>
          </div>
        </transition>
        <p v-if="showCaptions && captionText(item) && loadedFlags[index]" class="gallery-caption">{{ captionText(item) }}</p>
        <!-- "+N more" overlay on last visible item if there are more -->
      </div>
    </template>
  </MasonryWall>
  <WebButton
      v-if="props.media.length > props.maxVisible"
      class="absolute "
      @click.stop="openAllLightbox(props.maxVisible)"
  >
    +{{ props.media.length - props.maxVisible }} more
  </WebButton>
  </div>
  <LightBox
      ref="lightbox"
      :items="media.map(m => ({ type: m.type, url: m.type === 'image' ? resolveMediaUrl(m.image.url) : resolveVideo(m.video) }))"
      frosted
  />
</template>

<script setup>
import {useImageAlt} from "@/composables/useImageAlt.js";
const imgAlt = useImageAlt();
import {imgAttrs} from "@/utils/photoSizes.js";
import { computed, ref, onMounted, nextTick } from 'vue'
import MasonryWall from '@yeger/vue-masonry-wall'
import LightBox from '@/components/LightBox.vue'
import { resolveMediaUrl, resolveVideo } from '@/composables/images.js'
import WebButton from "@/components/WebButton.vue";
import { captionText } from "@/utils/sectionMedia.js";
import {storeToRefs} from "pinia";
import {useColorStore} from "@/classes/useColorStore.js";

const props = defineProps({
  media: { type: Array, required: true },
  maxVisible: { type: Number, default: 5 },
  colors: { type: Object, required: true },
  showCaptions: { type: Boolean, default: false }
})

const { theme } = storeToRefs(useColorStore());


const lightbox = ref(null)
const masonryRef = ref(null)

const layoutReady = ref(false)

const visibleItems = computed(() => props.media.filter(x => x.type !== 'video1').slice(0, props.maxVisible))

const preloadedItems = ref(visibleItems.value.map(() => ({})))
const loadedFlags = ref(visibleItems.value.map(() => false))

const showMedia = computed(() => props.media.filter(x => x.image?.url !== '' || x.video?.link !== '').length > 0)

const mediaRadiusClass = computed(() => {
  switch (theme.value) {
    case 'glass':
    case 'neon':
      return 'rounded-md';
    case 'corporate':
      return 'rounded-sm';
    default:
      return '';
  }
});

const wallClass = computed(() => {
  switch (theme.value) {
    case 'glass':
      return 'w-full border-1 border-[var(--color-accent)] bg-[var(--color-accent)]/10 py-12 rounded-lg px-4';
    case 'brutalist':
      return 'w-full border-4 border-black py-12 px-4';
    case 'neon':
      return 'w-full border border-[var(--color-accent)] shadow-[0_0_20px_var(--color-accent)] py-12 px-4 rounded-md';
    case 'corporate':
      return 'w-full border border-neutral-200 py-12 px-4 rounded-sm';
    case 'flat':
    default:
      return 'w-full py-12 px-4';
  }
});

// const mediaClass = computed(() => {
//   return theme.value === 'glass' ? 'border-2 border-[var(--color-accent)] rounded-lg shadow-lg bg-[var(--bg-light)]/80 dark2:bg-[var(--bg-dark)]' :
//       '';
// })

function preloadImageWithDimensions(src) {
  return new Promise((resolve) => {
    const img = new Image()
    img.src = src
    img.onload = () => {
      resolve({
        resolvedUrl: src,
        width: img.naturalWidth,
        height: img.naturalHeight * 0.8,
        aspectRatio: img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1
      })
    }
    img.onerror = () => {
      resolve({
        resolvedUrl: src,
        width: 0,
        height: 0,
        aspectRatio: 1
      })
    }
  })
}

function preloadVideoWithDimensions(videoObj) {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.src = resolveVideo(videoObj)
    video.onloadedmetadata = () => {
      const width = video.videoWidth
      const height = video.videoHeight
      resolve({
        resolvedUrl: resolveVideo(videoObj),
        video: videoObj,
        width,
        height,
        aspectRatio: width && height ? width / height : 1
      })
    }
    video.onerror = () => {
      resolve({
        resolvedUrl: resolveVideo(videoObj),
        video: videoObj,
        width: 0,
        height: 0,
        aspectRatio: 1
      })
    }
  })
}

async function preloadMediaItemsWithDimensions(items) {
  const promises = items.map(async (item, i) => {
    if (item.type === 'image') {
      const res = await preloadImageWithDimensions(resolveMediaUrl(item.image.url))
      preloadedItems.value[i] = {
        ...item,
        resolvedUrl: res.resolvedUrl,
        width: res.width,
        height: res.height,
        aspectRatio: res.aspectRatio
      }
      loadedFlags.value[i] = true
    } else if (item.type === 'video') {
      const res = await preloadVideoWithDimensions(item.video)
      preloadedItems.value[i] = {
        ...item,
        resolvedUrl: res.resolvedUrl,
        video: res.video,
        width: res.width,
        height: res.height,
        aspectRatio: res.aspectRatio
      }
      loadedFlags.value[i] = true
    } else {
      preloadedItems.value[i] = {
        ...item,
        resolvedUrl: '',
        width: 0,
        height: 0,
        aspectRatio: 1
      }
      loadedFlags.value[i] = true
    }
  })
  await Promise.all(promises)
}

onMounted(async () => {
  // preloadedItems and loadedFlags already initialized with empty and false values
  await preloadMediaItemsWithDimensions(visibleItems.value)
  layoutReady.value = true
  // Wait for nextTick to update Masonry layout with aspect ratios and prevent layout shift
  await nextTick()
  await new Promise(r => setTimeout(r, 100))
  if (masonryRef.value && masonryRef.value.$el) {
    masonryRef.value.$el.dispatchEvent(new Event('resize'))
  }
})

const cssVars = computed(() => ({
  "--bg-light": !props.forceBlandColors && props.backgroundVariant === 2 ? "var(--brand-lightest)" : "var(--brand-whitest)",
  "--bg-dark": "var(--brand-darkest)",
  "--color-accent": "var(--brand-accent)"
}));

function openAllLightbox(pindex = null) {
  const startIndex = pindex !== null ? pindex : props.maxVisible
  lightbox.value?.openBox(props.media, startIndex)
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 1.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.fade-enter-to, .fade-leave-from {
  opacity: 1;
}

/* Shared base styles for media */
.masonry-media {
  /*max-height: 80dvh;*/
  width: 100%;
  height: 100%;
  display: block;
}

img.masonry-media {
  object-fit: contain;
}

video.masonry-media {
  object-fit: contain;
  background-color: black;
}

/* Gallery captions: over the foot of each photo in the wall (its height is
   measured from the photo), under it for a single photo. */
.gallery-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  margin: 0;
  padding: 18px 10px 8px;
  font-size: 14px;
  line-height: 1.3;
  color: #fff;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0));
  pointer-events: none;
}
.gallery-caption-below {
  margin: 8px 0 0;
  text-align: center;
  font-size: 15px;
  color: var(--theme-text-body, inherit);
}
</style>